import { getToken, onMessage, type MessagePayload } from "firebase/messaging";
import type { Messaging } from "firebase/messaging";
import { getFirebaseMessaging } from "./firebase";

const VAPID_KEY =
  "BJa4NXV5WLQx8SBwlLzyjPihrqewalnj_cbAt_XL3AIHufybrkbxQgEvKBDOvAMumJ5lWPzw-O-3oQX_0KYWg6k";

const hasBrowserNotificationSupport = (): boolean => {
  return typeof window !== "undefined" && "Notification" in window;
};

const ensureNotificationPermission = async (): Promise<boolean> => {
  if (!hasBrowserNotificationSupport()) {
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission === "denied") {
    return false;
  }

  const permission: NotificationPermission =
    await Notification.requestPermission();
  return permission === "granted";
};

const getOrRegisterServiceWorker = async (): Promise<
  ServiceWorkerRegistration | undefined
> => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return undefined;
  }

  const registrationUrl = "/firebase-messaging-sw.js";

  const readyRegistration = async (): Promise<
    ServiceWorkerRegistration | undefined
  > => {
    try {
      return await navigator.serviceWorker.ready;
    } catch {
      return undefined;
    }
  };

  let registration =
    await navigator.serviceWorker.getRegistration(registrationUrl);

  if (registration?.active) {
    return registration;
  }

  if (!registration) {
    try {
      registration = await navigator.serviceWorker.register(registrationUrl, {
        scope: "/",
      });
    } catch (error) {
      console.error(
        "Failed to register firebase messaging service worker",
        error,
      );
      return undefined;
    }
  }

  if (registration.active) {
    return registration;
  }

  const ready = await readyRegistration();
  return ready ?? registration;
};

export const unregisterFirebaseMessagingServiceWorker = async (): Promise<void> => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration(
      "/firebase-messaging-sw.js",
    );
    await registration?.unregister();
  } catch (error) {
    console.error("Failed to unregister firebase messaging service worker", error);
  }
};

export const getWebFcmToken = async (): Promise<string | null> => {
  try {
    const messaging: Messaging | null = await getFirebaseMessaging();

    if (!messaging) {
      console.warn("Firebase Messaging is not supported in this environment");
      return null;
    }

    const hasPermission = await ensureNotificationPermission();
    if (!hasPermission) {
      console.warn("Notification permission was not granted by the user");
      return null;
    }

    const serviceWorkerRegistration = await getOrRegisterServiceWorker();

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    });

    return token || null;
  } catch (err) {
    console.error("Web FCM error", err);
    return null;
  }
};

export const listenForWebForegroundMessages = async (
  listener: (payload: MessagePayload) => void,
): Promise<() => void> => {
  const messaging: Messaging | null = await getFirebaseMessaging();

  if (!messaging) {
    return () => {};
  }

  return onMessage(messaging, listener);
};
